import { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { useTable, useSortBy, SortingRule, Column } from "react-table";
import { TooltipContainer } from "@outplayed/tooltips";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { TableScrollHandler, useLazyLoadTableRows } from "@ugg/shared/components/TierList/common/TableScrollHandler";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getTierColor, getLuTierColor, getLuTier } from "@ugg/shared/utils/tier-helpers";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { RoleC, getApiRole, getRoleLabel } from "@ugg/shared/utils/role-helpers";
import { DuoInfo } from "@ugg/shared/api/data-parser/tier-lists/duo-tier-list";
import { DuoRole } from "@ugg/shared/interfaces/tier-lists/duo-tier-list.interface";

interface DuoTierListTableProps {
  data: Record<DuoRole, DuoInfo[]>;
  filters: {
    duo: DuoRole;
  };
}

export const DuoTierListTable = (props: DuoTierListTableProps) => {
  const { getChampionName } = getRiotAssetsContext();
  const prevSort = useRef<SortingRule<DuoInfo>[] | null>(null);
  const maxPageSize = 50;
  const [pageSize, setPageSize] = useState(maxPageSize);
  const { data, filters } = props;

  const duoData = useMemo(() => {
    const role1 = getApiRole(filters.duo.split("_")[0]);
    const role2 = getApiRole(filters.duo.split("_")[1]);
    return data?.[`${role1}_${role2}` as DuoRole] || [];
  }, [data, filters]);

  const role1 = getRoleLabel(duoData?.[0]?.["duo_1_role"]);
  const role2 = getRoleLabel(duoData?.[0]?.["duo_2_role"]);

  const columns = useMemo<Column<DuoInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "duo_1",
        Header: "Rank",
        responsiveClassName: "xxxs:min-w-[50px] xxxs:max-w-[50px]",
        headerClassName: "rank xxxs:max-xs:invisible",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: role1,
        className: "champion",
        headerClassName: "champion",
        responsiveClassName: "max-md:min-w-[60px] md:min-w-[120px] max-w-[150px]",
        accessor: "duo_1_link",
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.duo_1_link.championId);
          const champNameB = getChampionName(b.values.duo_1_link.championId);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ value }) => (
          <ChampionLink
            className="max-md:[&_.champion-name]:hidden max-md:!pl-0 xs:[&_.role]:hidden"
            championId={value.championId}
            role={RoleC.convertToString(value.role)}
            dim={30}
          />
        ),
      },
      {
        Header: () => (
          <span>
            {role1}
            <br />
            Win Rate
          </span>
        ),
        headerClassName: "duo-winrate",
        className: "duo-winrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_1_winrate",
        Cell: ({ value }) => <span>{value.toFixed(2) + "%"}</span>,
      },
      {
        Header: `${role2}`,
        className: "champion",
        headerClassName: "champion",
        responsiveClassName: "max-md:min-w-[60px] md:min-w-[120px] max-w-[150px]",
        accessor: "duo_2_link",
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.duo_2_link.championId);
          const champNameB = getChampionName(b.values.duo_2_link.championId);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ value }) => (
          <ChampionLink
            className="max-md:[&_.champion-name]:hidden max-md:!pl-0 xs:[&_.role]:hidden"
            championId={value.championId}
            role={RoleC.convertToString(value.role)}
            dim={30}
          />
        ),
      },
      {
        Header: () => (
          <span>
            {role2}
            <br />
            Win Rate
          </span>
        ),
        className: "duo-winrate",
        headerClassName: "duo-winrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_2_winrate",
        Cell: ({ value }) => <span>{value.toFixed(2) + "%"}</span>,
      },
      {
        Header: () => (
          <TooltipContainer
            tooltipInfo={`The U.GG tier list is derived from standard deviations on
            champion winrates, synergy factor, pick rates and ban rates to identify the
            strongest champions in the current meta.`}
          >
            <div>Tier</div>
          </TooltipContainer>
        ),
        id: "tier",
        headerClassName: "tier",
        className: "tier",
        responsiveClassName: "min-w-[70px]",
        accessor: "stdevs",
        sortType: (a, b) => a.values.tier - b.values.tier,
        Cell: ({ value }) => (
          <span className={getLuTierColor(Number(value?.toFixed(2)))}>
            <b>{getLuTier(Number(value?.toFixed(2)))}</b>
          </span>
        ),
      },
      {
        Header: () => (
          <span>
            Duo
            <br />
            Winrate
          </span>
        ),
        id: "duo_combined_winrate",
        className: "winrate",
        headerClassName: "winrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_combined_winrate",
        Cell: ({ value }) => (
          <span className={getTierColor(Number(value.toFixed(2)))}>
            <b>{Number(value.toFixed(2)) + "%"}</b>
          </span>
        ),
      },
      {
        Header: () => (
          <TooltipContainer
            tooltipInfo={
              <div style={{ width: 300 }}>
                The duo winrate minus the square root of the product of the two individual winrates. Used to understand how well
                two champions work and win together.
              </div>
            }
          >
            <div>
              <span>
                Synergy
                <br />
                Factor
              </span>
            </div>
          </TooltipContainer>
        ),
        className: "",
        headerClassName: "plus-minus-winrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "plus_minus_winrate",
        Cell: ({ value }) => <span>{(value > 0 ? "+" : "") + value.toFixed(2) + "%"}</span>,
      },
      {
        Header: () => (
          <span>
            Duo
            <br />
            Pick Rate
          </span>
        ),
        className: "pickrate",
        headerClassName: "pickrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_combined_pickrate",
        Cell: ({ value }) => <span>{value.toFixed(2) + "%"}</span>,
      },
      {
        Header: () => (
          <TooltipContainer
            tooltipInfo={
              <div style={{ width: 250 }}>The percentage of games where at least one of the two champions are banned.</div>
            }
          >
            <div>
              <span>
                Duo
                <br />
                Ban Rate
              </span>
            </div>
          </TooltipContainer>
        ),
        className: "banrate",
        headerClassName: "banrate",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_combined_banrate",
        Cell: ({ value }) => <span>{Number(value.toFixed(2)) + "%"}</span>,
      },
      {
        Header: "Matches",
        responsiveClassName: "min-w-[70px]",
        accessor: "duo_combined_matches",
        Cell: ({ value }) => <span>{numberWithCommas(Number(value))}</span>,
      },
    ],
    [role1, role2],
  );

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, preSortedRows, prepareRow } = useTable(
    {
      columns,
      data: duoData,
      initialState: {
        sortBy: prevSort.current || [{ id: "duo_combined_winrate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  useEffect(() => {
    setPageSize(maxPageSize);
  }, [duoData]);

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData: duoData,
    tableRef,
    pageSize,
    setPageSize,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(maxPageSize);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
        <div
          ref={tableRef}
          {...getTableProps({ className: "content-section ReactTable ugg-table-2 duo-tier-list-table min-w-[900px]" })}
        >
          <div className={`rt-thead -header`}>
            {headerGroups.map((headerGroup, index) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
                {headerGroup.headers.map((column, index) => {
                  const thClassNames = classnames([
                    "rt-th",
                    column.headerClassName,
                    column.responsiveClassName,
                    column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                    column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                  ]);

                  return (
                    <div className={thClassNames} {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, i) => {
              if (i >= pageSize) {
                return null;
              }
              prepareRow(row);
              return (
                <div key={i} className="rt-tr-group">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, index) => {
                      const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      });

                      return <div {...cell.getCellProps({ className: tdClassNames })}>{cell.render("Cell", { index: i })}</div>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <TableScrollHandler />
    </div>
  );
};
